/**
 * Hook para manejo de códigos QR
 * Proporciona funcionalidades para generar y gestionar QR codes
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { generateQRCode, generateQRDataURL, QRCodeOptions, clearQRCache } from '@/utils/qr.utils';
import receiveService from '@/services/wallet/receive.service';

export interface UseQRCodeOptions extends QRCodeOptions {
  autoGenerate?: boolean;
  cacheEnabled?: boolean;
}

export interface QRCodeState {
  isLoading: boolean;
  isGenerated: boolean;
  error: string | null;
  dataURL: string | null;
}

export interface UseQRCodeReturn {
  // Estado
  state: QRCodeState;
  
  // Funciones
  generateQR: (address: string, canvas?: HTMLCanvasElement) => Promise<void>;
  generateQRDataURL: (address: string) => Promise<string | null>;
  clearError: () => void;
  clearCache: () => void;
  
  // Refs
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
}

/**
 * Hook para manejo de códigos QR
 */
export function useQRCode(options: UseQRCodeOptions = {}): UseQRCodeReturn {
  // Estado del QR
  const [state, setState] = useState<QRCodeState>({
    isLoading: false,
    isGenerated: false,
    error: null,
    dataURL: null,
  });

  // Ref para el canvas
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Opciones por defecto
  const qrOptions: QRCodeOptions = {
    size: options.size || 256,
    margin: options.margin || 4,
    colorDark: options.colorDark || '#000000',
    colorLight: options.colorLight || '#ffffff',
    errorCorrectionLevel: options.errorCorrectionLevel || 'H',
  };

  /**
   * Genera un código QR en el canvas
   */
  const generateQR = useCallback(
    async (address: string, canvas?: HTMLCanvasElement): Promise<void> => {
      if (!address) {
        setState(prev => ({
          ...prev,
          error: 'Dirección requerida para generar QR',
        }));
        return;
      }

      const targetCanvas = canvas || canvasRef.current;
      if (!targetCanvas) {
        setState(prev => ({
          ...prev,
          error: 'Canvas no disponible',
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      try {
        await generateQRCode(address, targetCanvas, qrOptions);
        
        // Obtener data URL del canvas
        const dataURL = targetCanvas.toDataURL('image/png');
        
        setState(prev => ({
          ...prev,
          isLoading: false,
          isGenerated: true,
          dataURL,
        }));

        console.log('[useQRCode] QR generado exitosamente para:', address);
      } catch (error) {
        console.error('[useQRCode] Error generando QR:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          isGenerated: false,
          error: (error as Error).message,
        }));
      }
    },
    [qrOptions.size, qrOptions.margin, qrOptions.colorDark, qrOptions.colorLight, qrOptions.errorCorrectionLevel]
  );


  /**
   * Genera un código QR como Data URL sin canvas
   */
  const generateQRDataURLOnly = useCallback(
    async (address: string): Promise<string | null> => {
      if (!address) {
        setState(prev => ({
          ...prev,
          error: 'Dirección requerida para generar QR',
        }));
        return null;
      }

      setState(prev => ({
        ...prev,
        isLoading: true,
        error: null,
      }));

      try {
        const result = await generateQRDataURL(address, qrOptions);
        
        if (result.success && result.dataURL) {
          setState(prev => ({
            ...prev,
            isLoading: false,
            isGenerated: true,
            dataURL: result.dataURL!,
          }));

          console.log('[useQRCode] QR Data URL generado exitosamente');
          return result.dataURL;
        } else {
          throw new Error(result.error || 'Error generando QR');
        }
      } catch (error) {
        console.error('[useQRCode] Error generando QR Data URL:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          isGenerated: false,
          error: (error as Error).message,
        }));
        return null;
      }
    },
    [qrOptions.size, qrOptions.margin, qrOptions.colorDark, qrOptions.colorLight, qrOptions.errorCorrectionLevel]
  );


  /**
   * Limpia el error actual
   */
  const clearError = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: null,
    }));
  }, []);

  /**
   * Limpia el cache de QR codes
   */
  const clearCacheCallback = useCallback(() => {
    if (options.cacheEnabled !== false) {
      clearQRCache();
      console.log('[useQRCode] Cache limpiado');
    }
  }, [options.cacheEnabled]);

  /**
   * Efecto para limpiar el estado cuando se desmonta el componente
   */
  useEffect(() => {
    return () => {
      // Limpiar estado al desmontar
      setState({
        isLoading: false,
        isGenerated: false,
        error: null,
        dataURL: null,
      });
    };
  }, []);

  return {
    state,
    generateQR,
    generateQRDataURL: generateQRDataURLOnly,
    clearError,
    clearCache: clearCacheCallback,
    canvasRef,
  };
}

/**
 * Hook simplificado para generar QR de una dirección específica
 */
export function useAddressQR(
  address: string | null,
  options: UseQRCodeOptions = {}
): UseQRCodeReturn & {
  regenerate: () => Promise<void>;
} {
  const qrHook = useQRCode(options);

  const { canvasRef, generateQR, generateQRDataURL } = qrHook;

  /**
   * Regenera el QR para la dirección actual
   */
  const regenerate = useCallback(async () => {
    if (address && canvasRef.current) {
      await generateQR(address);
    }
  }, [address, canvasRef, generateQR]);

  /**
   * Efecto para generar automáticamente cuando cambia la dirección
   */
  useEffect(() => {
    if (options.autoGenerate !== false && address) {
      console.log(`[useAddressQR] Auto-generating QR for ${address}`);
      if (canvasRef.current) {
        generateQR(address).catch(err => {
          console.error('[useAddressQR] Error generating QR on canvas:', err);
        });
      } else {
        console.log('[useAddressQR] No canvas found, using DataURL fallback');
        generateQRDataURL(address).catch(err => {
          console.error('[useAddressQR] Error generating QR DataURL:', err);
        });
      }
    }
  }, [address, options.autoGenerate, canvasRef, generateQR, generateQRDataURL]);

  return {
    ...qrHook,
    regenerate,
  };
}

/**
 * Hook para QR de recepción (usa la dirección actual de la wallet)
 */
export function useReceiveQR(options: UseQRCodeOptions = {}): UseQRCodeReturn & {
  address: string | null;
  network: string | null;
  refreshAddress: () => void;
} {
  const [address, setAddress] = useState<string | null>(null);
  const [network, setNetwork] = useState<string | null>(null);
  const qrHook = useQRCode(options);

  const { canvasRef, generateQR } = qrHook;

  /**
   * Obtiene la dirección actual de la wallet
   */
  const refreshAddress = useCallback(async () => {
    try {
      const addressInfo = receiveService.getCurrentReceiveAddress();
      
      if (addressInfo) {
        setAddress(addressInfo.address);
        setNetwork(addressInfo.network);
        
        // Generar QR automáticamente si está habilitado
        if (options.autoGenerate !== false && canvasRef.current) {
          await generateQR(addressInfo.address);
        }
      } else {
        setAddress(null);
        setNetwork(null);
      }
    } catch (error) {
      console.error('[useReceiveQR] Error obteniendo dirección:', error);
      setAddress(null);
      setNetwork(null);
    }
  }, [options.autoGenerate, canvasRef, generateQR]);

  /**
   * Efecto para obtener la dirección al montar
   */
  useEffect(() => {
    refreshAddress();
  }, [refreshAddress]);

  /**
   * Efecto para escuchar cambios de wallet/red
   */
  useEffect(() => {
    const handleWalletChange = () => {
      refreshAddress();
    };

    // Escuchar eventos de cambio
    document.addEventListener('wallet:unlocked', handleWalletChange);
    document.addEventListener('wallet:locked', handleWalletChange);
    document.addEventListener('network:changed', handleWalletChange);

    return () => {
      document.removeEventListener('wallet:unlocked', handleWalletChange);
      document.removeEventListener('wallet:locked', handleWalletChange);
      document.removeEventListener('network:changed', handleWalletChange);
    };
  }, [refreshAddress]);

  return {
    ...qrHook,
    address,
    network,
    refreshAddress,
  };
}

export default useQRCode;